import React, { useRef } from 'react';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import VideoCom from '$/components/VideoCom';
import { AuditStatus, CommunityCardItem } from '$/domains/community/type';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import useThemeStore from '$/store/useThemeStore';
import { getVideoCoverUrl } from '$/utils/upload';
import { navigate } from '@/common/agents';
import { useCardExpo } from '@/hooks/useCardExpo';
import { useCommonStore } from '@/store/useCommonStore';
import cn from 'classnames';
import AudioPlayer, { AudioPlayerData } from '$/components/AudioPlayer';
import styles from './index.module.scss';
import { CategoryItemId } from '..';

interface PropsType {
  item: CommunityCardItem;
  activeTabId: string | number;
  activeTabName?: string;
  currentIndex: number;
  stopAudioPlaying: boolean;
  onAudioPlayStateChange: (data: AudioPlayerData) => void;
}

interface VideoControls {
  play: () => void;
  pause: () => void;
}

export const AuditStatusTrack = {
  [AuditStatus.Auditing]: 'doing',
  [AuditStatus.Resolved]: 'publish',
  [AuditStatus.Rejected]: 'refuse',
};

function WorkItem({
  item,
  activeTabId,
  activeTabName,
  currentIndex,
  stopAudioPlaying,
  onAudioPlayStateChange,
}: PropsType) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const theme = useThemeStore((state) => state.theme);
  const mediaType = item.cover_media?.[0]?.type;

  const videoControlRef = useRef<VideoControls | null>(null);

  const AuditStatusName = {
    [AuditStatus.Auditing]: languageStore.smartcatTranslationText(
      (v) => v.roboneo_web_under_review,
    ),
    [AuditStatus.Resolved]: languageStore.smartcatTranslationText(
      (v) => v.roboneo_web_review_approved,
    ),
    [AuditStatus.Rejected]: languageStore.smartcatTranslationText(
      (v) => v.roboneo_web_review_rejected,
    ),
  };

  const cardExpo = useCardExpo<HTMLDivElement>({
    onExpo: () => {
      window.mtstat?.track('roboneo_case_card_show', {
        tab_id: activeTabId,
        tab_name: activeTabName,
        content_type: 'workflow',
        content_id: item.community_id,
        author_id: item.user?.uid,
        used_num: item.used_num?.match(/\d+/)?.[0] || item.used_num,
        position_id: currentIndex,
        content_status:
          activeTabId === CategoryItemId.mine
            ? AuditStatusTrack[item.audit_status]
            : AuditStatusTrack[AuditStatus.Resolved],
      });
    },
    isActive: true,
  });

  // 点击跳转
  const handleClick = async () => {
    window.mtstat?.track('roboneo_case_card_click', {
      tab_id: activeTabId,
      tab_name: activeTabName,
      content_type: 'workflow',
      content_id: item.community_id,
      author_id: item.user?.uid,
      used_num: item.used_num?.match(/\d+/)?.[0] || item.used_num,
      position_id: currentIndex,
      content_status:
          activeTabId === CategoryItemId.mine
            ? AuditStatusTrack[item.audit_status]
            : AuditStatusTrack[AuditStatus.Resolved],
    });

    if (!isUserLoggedIn) {
      await toLogin().then(() => {
        initUserInfo();
      });
      navigate({
        mode: 'url',
        openNewWindow: true,
        url: {
          originId: 'roboneo.com',
          pathname: `/community/${
            activeTabId === CategoryItemId.used ? item.from_room_id : item.room_id
          }`,
        },
      });
      return;
    }
    navigate({
      mode: 'url',
      openNewWindow: true,
      url: {
        originId: 'roboneo.com',
        pathname: `/community/${
          activeTabId === CategoryItemId.used ? item.from_room_id : item.room_id
        }`,
      },
    });
  };

  const getImageUrl = (url: string) => {
    if (mediaType === 'image') {
      return url;
    }
    if (mediaType === 'video') {
      return getVideoCoverUrl(url);
    }
    return '';
  };

  const mediaUrl = item.cover_media?.[0]?.url;

  return (
    <div key={item.room_id} className={styles.workItem} ref={cardExpo}>
      <div
        className={styles.imageBox}
        onClick={handleClick}
      >
        {mediaType === 'audio' && (
          <AudioPlayer
            className={cn(styles.audioPlayer, {
              [styles.audioBgLight]: mediaType === 'audio' && theme === 'light',
              [styles.audioBgDark]: mediaType === 'audio' && theme === 'dark',
            })}
            mode="full"
            url={mediaUrl}
            stopPlaying={stopAudioPlaying}
            autoHideController
            showDownload={false}
            onPlayStateChange={onAudioPlayStateChange}
          >
            <div className={styles.audioIcon} />
          </AudioPlayer>
        )}

        {mediaType === 'image' && (
          <NextImage
            height="100%"
            className={styles.itemImage}
            sizes="370px"
            alt=""
            src={getImageUrl(mediaUrl)}
          />
        )}

        {mediaType === 'video' && (
          <div
            className={styles.videoBox}
            onMouseEnter={() => videoControlRef.current?.play()}
            onMouseLeave={() => videoControlRef.current?.pause()}
          >
            <VideoCom
              volume={0}
              url={mediaUrl}
              poster={getImageUrl(mediaUrl)}
              autoplay={false}
              closeVideoClick
              className={styles.videoContainer}
              videoClassName={styles.videoEle}
            >
              {({ play, pause }) => {
                videoControlRef.current = { play, pause };
                return <div />;
              }}
            </VideoCom>
          </div>
        )}

        {activeTabId === CategoryItemId.mine && (
          <div className={styles.itemType}>
            {AuditStatusName[item.audit_status]}
          </div>
        )}
      </div>
      <div className={styles.title}>{item.name}</div>
      <div className={styles.desc}>{item.description}</div>
      <div className={styles.bottomBox}>
        <div className={styles.creatorBox}>
          <NextImage
            className={styles.avatar}
            sizes="370px"
            alt=""
            src={item.user?.avatar}
            width={28}
            height={28}
          />
          <div className={styles.name}>{item.user?.screen_name}</div>
        </div>
        <div className={styles.useBox}>
          <IconFont
            name="userGroupV3Fill"
            fontSize={16}
            className={styles.userIcon}
          />
          <div>{item.used_num}</div>
        </div>
      </div>
    </div>
  );
}

export default WorkItem;
